// src/features/calendar/store/calendarStore.ts

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { io, type Socket } from 'socket.io-client';
import type { 
  EdgeCalendar, 
  CalendarEvent, 
  CourtRuleEvent,
  CalendarView, 
  EventFilters
} from '../types/calendar.types';

// MCP and WebSocket Types
interface MCPCalendarState {
  isConnected: boolean;
  connectionId: string | null;
  lastHeartbeat: Date | null;
  mcpSessionId: string | null;
}

interface WebSocketState {
  isConnected: boolean;
  reconnectAttempts: number;
  maxReconnectAttempts: number;
  reconnectDelay: number;
}

interface MCPCommand {
  command: string;
  params: Record<string, unknown>;
}

interface MCPResponse {
  success: boolean;
  data?: unknown;
  error?: string;
}

interface WebSocketMessage {
  type: string;
  payload: unknown;
  timestamp: Date;
}

interface RealTimeUpdate {
  id: string;
  type: 'event_created' | 'event_updated' | 'event_deleted' | 'calendar_shared' | 'permission_changed';
  data: CalendarEvent | EdgeCalendar | Record<string, unknown> | string;
  timestamp: Date;
  userId: string;
}

interface CalendarRealtimeEnvelope {
  schema_version: '1.0';
  realm_id: string;
  calendar_id: string;
  event_type:
    | 'calendar.event.created'
    | 'calendar.event.updated'
    | 'calendar.event.deleted'
    | 'calendar.permission.changed'
    | 'calendar.attendee.rsvp.changed'
    | 'calendar.sync.status';
  entity_id: string;
  actor_user_id: string;
  occurred_at: string;
  payload: unknown;
}

// 🔧 Helpers for date normalization and type guard
const normalizeEvent = (event: CalendarEvent): CalendarEvent => {
  const toDate = (d: unknown): Date =>
    d instanceof Date ? d : new Date(d as string | number);
  return {
    ...event,
    start: toDate(event.start),
    end: toDate(event.end),
  };
};

const isCalendarEvent = (obj: unknown): obj is CalendarEvent => {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'id' in obj &&
    'start' in obj &&
    'end' in obj &&
    'title' in obj &&
    'calendarId' in obj
  );
};

let calendarSocket: Socket | null = null;

const JWT_PATTERN = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

const getAuthTokenForSocket = (): string => {
  const windowToken = (window as any)?.keycloak?.token
    || (window as any)?.edgelexKeycloak?.token
    || (window as any)?.edgelexToken;

  if (typeof windowToken === 'string' && JWT_PATTERN.test(windowToken)) {
    return windowToken;
  }

  for (const storage of [sessionStorage, localStorage]) {
    for (const key of Object.keys(storage)) {
      const value = storage.getItem(key);
      if (!value) continue;

      if (JWT_PATTERN.test(value)) {
        return value;
      }

      if (value.startsWith('{')) {
        try {
          const parsed = JSON.parse(value);
          const tokenFields = ['token', 'access_token', 'accessToken', 'idToken', 'id_token'];
          for (const field of tokenFields) {
            if (parsed[field] && JWT_PATTERN.test(parsed[field])) {
              return parsed[field];
            }
          }
        } catch {
          // Ignore non-JSON values.
        }
      }
    }
  }

  return '';
};

const mapBackendEventToCalendarEvent = (raw: Record<string, any>): CalendarEvent => ({
  id: String(raw.id),
  title: raw.title || 'Untitled Event',
  start: new Date(raw.start_time ?? raw.start ?? Date.now()),
  end: new Date(raw.end_time ?? raw.end ?? Date.now()),
  allDay: Boolean(raw.all_day ?? raw.allDay ?? false),
  description: raw.description || '',
  descriptionJson: raw.description_json ?? raw.descriptionJson,
  location: raw.location || '',
  calendarId: String(raw.calendar_id ?? raw.calendarId ?? ''),
  matterId: raw.matter_id ?? raw.matterId,
  contactIds: Array.isArray(raw.contact_ids) ? raw.contact_ids : raw.contactIds,
  attendees: Array.isArray(raw.attendees) ? raw.attendees : [],
  eventTypeId: raw.event_type ?? raw.eventTypeId ?? 'meeting',
  priority: raw.priority ?? 'medium',
  recurringRule: raw.recurring_rule ?? raw.recurringRule,
  recurringEventId: raw.recurring_event_id ?? raw.recurringEventId,
  zoomLink: raw.meeting_integrations?.zoom_url ?? raw.zoomLink,
  googleMeetLink: raw.meeting_integrations?.meet_url ?? raw.googleMeetLink,
  teamsLink: raw.meeting_integrations?.teams_url ?? raw.teamsLink,
  createdBy: raw.created_by ?? raw.createdBy ?? 'system',
  createdAt: new Date(raw.created_at ?? raw.createdAt ?? Date.now()),
  updatedAt: new Date(raw.updated_at ?? raw.updatedAt ?? Date.now()),
  isDeleted: Boolean(raw.deleted_at ?? raw.isDeleted ?? false),
});

const isCalendarRealtimeEnvelope = (obj: unknown): obj is CalendarRealtimeEnvelope => {
  if (!obj || typeof obj !== 'object') return false;
  return (
    'schema_version' in obj &&
    'event_type' in obj &&
    'calendar_id' in obj &&
    'entity_id' in obj &&
    'actor_user_id' in obj
  );
};

const mapEnvelopeToRealtimeUpdate = (envelope: CalendarRealtimeEnvelope): RealTimeUpdate | null => {
  switch (envelope.event_type) {
    case 'calendar.event.created':
      if (envelope.payload && typeof envelope.payload === 'object') {
        return {
          id: envelope.entity_id,
          type: 'event_created',
          data: mapBackendEventToCalendarEvent(envelope.payload as Record<string, any>),
          timestamp: new Date(envelope.occurred_at),
          userId: envelope.actor_user_id,
        };
      }
      return null;

    case 'calendar.event.updated':
      if (envelope.payload && typeof envelope.payload === 'object') {
        return {
          id: envelope.entity_id,
          type: 'event_updated',
          data: mapBackendEventToCalendarEvent(envelope.payload as Record<string, any>),
          timestamp: new Date(envelope.occurred_at),
          userId: envelope.actor_user_id,
        };
      }
      return null;

    case 'calendar.event.deleted':
      return {
        id: envelope.entity_id,
        type: 'event_deleted',
        data: envelope.entity_id,
        timestamp: new Date(envelope.occurred_at),
        userId: envelope.actor_user_id,
      };

    case 'calendar.permission.changed':
      return {
        id: envelope.entity_id,
        type: 'permission_changed',
        data: (envelope.payload as Record<string, unknown>) || {},
        timestamp: new Date(envelope.occurred_at),
        userId: envelope.actor_user_id,
      };

    default:
      return null;
  }
};

interface CalendarState {
  // Calendar Management
  calendars: EdgeCalendar[];
  visibleCalendarIds: string[];
  selectedCalendarId: string | null;
  
  // Event Management  
  events: CalendarEvent[];
  selectedEventId: string | null;
  eventFilters: EventFilters;
  
  // UI State
  currentView: CalendarView;
  currentDate: Date;
  isEventFormOpen: boolean;
  isCalendarSidebarOpen: boolean;
  
  // Loading & Error States
  isLoading: boolean;
  error: string | null;
  
  // Permissions & Sharing
  userPermissions: Record<string, Record<string, unknown>>;
  sharedCalendars: EdgeCalendar[];
  
  // Court Rules & Legal Features
  courtRuleEvents: CourtRuleEvent[];
  deletedEvents: CalendarEvent[]; // Recovery bin
  
  // Real-time State
  lastSyncTime: Date | null;
  pendingChanges: string[]; // Event IDs with unsaved changes
  
  // MCP Orchestration State
  mcpState: MCPCalendarState;
  
  // WebSocket State
  wsState: WebSocketState;
  
  // Real-time Updates
  recentUpdates: RealTimeUpdate[];
  conflictingEvents: string[]; // Event IDs with conflicts
  
  // Drag & Drop State
  isDragging: boolean;
  draggedEventId: string | null;
  
  // Recurring Events State
  recurringEventSeries: Record<string, string[]>; // seriesId -> eventIds[]
  
  // API Sync State
  externalCalendarSyncs: Record<string, { lastSync: Date; status: 'syncing' | 'synced' | 'error' }>;
  
  // Actions
  setCurrentView: (view: CalendarView) => void;
  setCurrentDate: (date: Date) => void;
  setSelectedCalendarId: (id: string | null) => void;
  setSelectedEventId: (id: string | null) => void;
  setEventFormOpen: (isOpen: boolean) => void;
  setCalendarSidebarOpen: (isOpen: boolean) => void;
  setEventFilters: (filters: EventFilters) => void;
  
  // Data Loading Actions
  loadCalendarsFromApi: () => Promise<void>;
  loadEventsFromApi: (filters?: any) => Promise<void>;
  initializeCalendarData: () => Promise<void>;
  
  // Calendar Actions
  addCalendar: (calendar: EdgeCalendar) => void;
  updateCalendar: (id: string, updates: Partial<EdgeCalendar>) => void;
  deleteCalendar: (id: string) => void;
  toggleCalendarVisibility: (id: string) => void;
  setCalendars: (calendars: EdgeCalendar[]) => void;
  
  // Event Actions
  addEvent: (event: CalendarEvent) => void;
  updateEvent: (id: string, updates: Partial<CalendarEvent>) => void;
  deleteEvent: (id: string) => void;
  moveEvent: (id: string, start: Date, end: Date) => void;
  setEvents: (events: CalendarEvent[]) => void;
  
  // MCP Actions
  initializeMCP: () => Promise<void>;
  disconnectMCP: () => void;
  sendMCPCommand: (command: MCPCommand) => Promise<MCPResponse>;
  
  // WebSocket Actions
  connectWebSocket: () => void;
  disconnectWebSocket: () => void;
  handleWebSocketMessage: (message: WebSocketMessage) => void;
  
  // Real-time Actions
  handleRealTimeUpdate: (update: RealTimeUpdate) => void;
  resolveEventConflict: (eventId: string, resolution: 'accept' | 'reject') => void;
  
  // Drag & Drop Actions
  startDrag: (eventId: string) => void;
  endDrag: () => void;
  
  // Utility Actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
}

// Create the calendar store
const useCalendarStore = create<CalendarState>()(
  devtools(
    (set, get) => ({
      // Initial State - will be loaded from API
      calendars: [],
      visibleCalendarIds: [],
      selectedCalendarId: null,
      
      events: [],
      selectedEventId: null,
      eventFilters: {
        dateRange: null,
        priorities: [],
        eventTypes: [],
        attendees: []
      },
      
      currentView: 'month',
      currentDate: new Date(),
      isEventFormOpen: false,
      isCalendarSidebarOpen: true,
      
      isLoading: false,
      error: null,
      
      userPermissions: {},
      sharedCalendars: [],
      
      courtRuleEvents: [],
      deletedEvents: [],
      
      lastSyncTime: null,
      pendingChanges: [],
      
      mcpState: {
        isConnected: false,
        connectionId: null,
        lastHeartbeat: null,
        mcpSessionId: null,
      },
      
      wsState: {
        isConnected: false,
        reconnectAttempts: 0,
        maxReconnectAttempts: 5,
        reconnectDelay: 1000,
      },
      
      recentUpdates: [],
      conflictingEvents: [],
      
      isDragging: false,
      draggedEventId: null,
      
      recurringEventSeries: {},
      
      externalCalendarSyncs: {},
      
      // Basic Actions
      setCurrentView: (view) => set({ currentView: view }),
      setCurrentDate: (date) => set({ currentDate: date }),
      setSelectedCalendarId: (id) => set({ selectedCalendarId: id }),
      setSelectedEventId: (id) => set({ selectedEventId: id }),
      setEventFormOpen: (isOpen) => set({ isEventFormOpen: isOpen }),
      setCalendarSidebarOpen: (isOpen) => set({ isCalendarSidebarOpen: isOpen }),
      setEventFilters: (filters) => set({ eventFilters: filters }),
      
      // Calendar Actions
      addCalendar: (calendar) => {
        set((state) => ({
          calendars: [...(state.calendars || []), calendar],
          visibleCalendarIds: [...(state.visibleCalendarIds || []), calendar.id],
        }));

        if (calendarSocket && get().wsState.isConnected) {
          calendarSocket.emit('join-calendar', { calendarId: calendar.id });
        }
      },
      
      updateCalendar: (id, updates) => set((state) => ({
        calendars: (state.calendars || []).map(cal => 
          cal.id === id ? { ...cal, ...updates } : cal
        )
      })),
      
      deleteCalendar: (id) => set((state) => ({
        calendars: (state.calendars || []).filter(cal => cal.id !== id),
        visibleCalendarIds: (state.visibleCalendarIds || []).filter(calId => calId !== id),
        events: (state.events || []).filter(event => event.calendarId !== id)
      })),
      
      toggleCalendarVisibility: (id) => set((state) => {
        const currentlyVisible = (state.visibleCalendarIds || []).includes(id);
        if (calendarSocket && state.wsState.isConnected) {
          calendarSocket.emit(currentlyVisible ? 'leave-calendar' : 'join-calendar', { calendarId: id });
        }
        return {
          visibleCalendarIds: currentlyVisible
            ? (state.visibleCalendarIds || []).filter(calId => calId !== id)
            : [...(state.visibleCalendarIds || []), id],
        };
      }),
      
      setCalendars: (calendars) => {
        const visibleIds = (calendars || []).map(cal => cal.id);
        set({
          calendars: calendars || [],
          visibleCalendarIds: visibleIds,
        });

        if (calendarSocket && get().wsState.isConnected) {
          visibleIds.forEach((calendarId) => {
            calendarSocket?.emit('join-calendar', { calendarId });
          });
        }
      },
      
      // Event Actions
      addEvent: (event) => set((state) => ({
        events: [...(state.events || []), normalizeEvent(event)],
      })),
      
      updateEvent: (id, updates) => set((state) => ({
        events: (state.events || []).map(event => 
          event.id === id ? { ...event, ...updates } : event
        ),
        pendingChanges: (state.pendingChanges || []).includes(id) 
          ? (state.pendingChanges || [])
          : [...(state.pendingChanges || []), id]
      })),
      
      deleteEvent: (id) => set((state) => {
        const eventToDelete = (state.events || []).find(event => event.id === id);
        return {
          events: (state.events || []).filter(event => event.id !== id),
          deletedEvents: eventToDelete ? [...(state.deletedEvents || []), eventToDelete] : (state.deletedEvents || []),
          pendingChanges: (state.pendingChanges || []).filter(eventId => eventId !== id)
        };
      }),
      
      moveEvent: (id, start, end) => set((state) => ({
        events: (state.events || []).map(event => 
          event.id === id ? { ...event, start, end } : event
        ),
        pendingChanges: (state.pendingChanges || []).includes(id) 
          ? (state.pendingChanges || [])
          : [...(state.pendingChanges || []), id]
      })),
      
      setEvents: (events) => set({
        events: (events || []).map(normalizeEvent),
      }),
      
      // MCP Actions (with stubs for now)
      initializeMCP: async () => {
        console.log('🔗 Initializing MCP connection...');
        set((state) => ({
          mcpState: {
            ...state.mcpState,
            isConnected: true,
            connectionId: `mcp_${Date.now()}`,
            mcpSessionId: `session_${Date.now()}`,
            lastHeartbeat: new Date()
          }
        }));
        
        // TODO: Replace with actual MCP initialization
        // const mcpClient = await initializeMCPClient();
        // Handle MCP ready state
      },
      
      disconnectMCP: () => {
        console.log('🔌 Disconnecting MCP...');
        set((state) => ({
          mcpState: {
            ...state.mcpState,
            isConnected: false,
            connectionId: null,
            mcpSessionId: null
          }
        }));
      },
      
      sendMCPCommand: async (command) => {
        console.log('📡 Sending MCP command:', command);
        
        // TODO: Replace with actual MCP command sending
        // return await mcpClient.sendCommand(command.command, command.params);
        
        // Mock response for now
        return {
          success: true,
          data: { message: 'Command executed successfully' }
        };
      },
      
      // WebSocket Actions
      connectWebSocket: () => {
        if (calendarSocket && calendarSocket.connected) {
          return;
        }

        const token = getAuthTokenForSocket();
        if (!token) {
          get().setError('Unable to connect realtime calendar updates: missing auth token');
          return;
        }

        const { maxReconnectAttempts, reconnectDelay } = get().wsState;
        calendarSocket = io('/collaboration', {
          path: '/socket.io',
          auth: { token },
          transports: ['websocket'],
          reconnection: true,
          reconnectionAttempts: maxReconnectAttempts,
          reconnectionDelay: reconnectDelay,
        });

        calendarSocket.on('connect', () => {
          set((state) => ({
            wsState: {
              ...state.wsState,
              isConnected: true,
              reconnectAttempts: 0,
            },
            lastSyncTime: new Date(),
          }));

          (get().visibleCalendarIds || []).forEach((calendarId) => {
            calendarSocket?.emit('join-calendar', { calendarId });
          });
        });

        calendarSocket.on('disconnect', () => {
          set((state) => ({
            wsState: {
              ...state.wsState,
              isConnected: false,
            },
          }));
        });

        calendarSocket.io.on('reconnect_attempt', (attempt: number) => {
          set((state) => ({
            wsState: {
              ...state.wsState,
              reconnectAttempts: attempt,
            },
          }));
        });

        calendarSocket.on('calendar-realtime-event', (payload: unknown) => {
          get().handleWebSocketMessage({
            type: 'calendar_realtime_event',
            payload,
            timestamp: new Date(),
          });
        });

        calendarSocket.on('calendar-error', (payload: unknown) => {
          const message = typeof payload === 'object' && payload && 'error' in (payload as Record<string, unknown>)
            ? String((payload as Record<string, unknown>).error)
            : 'Calendar realtime error';
          get().setError(message);
        });
      },
      
      disconnectWebSocket: () => {
        if (calendarSocket) {
          calendarSocket.removeAllListeners();
          calendarSocket.disconnect();
          calendarSocket = null;
        }

        set((state) => ({
          wsState: {
            ...state.wsState,
            isConnected: false,
            reconnectAttempts: 0,
          },
        }));
      },
      
      handleWebSocketMessage: (message) => {
        switch (message.type) {
          case 'calendar_realtime_event':
            if (isCalendarRealtimeEnvelope(message.payload)) {
              const update = mapEnvelopeToRealtimeUpdate(message.payload);
              if (update) {
                get().handleRealTimeUpdate(update);
              }
            }
            break;

          case 'real_time_update':
            if (message.payload && typeof message.payload === 'object') {
              get().handleRealTimeUpdate(message.payload as RealTimeUpdate);
            }
            break;

          default:
            break;
        }
      },
      
      // Real-time Actions
      handleRealTimeUpdate: (update) => {
        set((state) => {
          const nextEvents = [...(state.events || [])];

          if (update.type === 'event_created' && isCalendarEvent(update.data)) {
            const createdEvent = normalizeEvent(update.data);
            const existingIdx = nextEvents.findIndex((event) => event.id === createdEvent.id);
            if (existingIdx >= 0) {
              nextEvents[existingIdx] = createdEvent;
            } else {
              nextEvents.push(createdEvent);
            }
          } else if (update.type === 'event_updated' && isCalendarEvent(update.data)) {
            const updatedEvent = normalizeEvent(update.data);
            const existingIdx = nextEvents.findIndex((event) => event.id === updatedEvent.id);
            if (existingIdx >= 0) {
              nextEvents[existingIdx] = updatedEvent;
            } else {
              nextEvents.push(updatedEvent);
            }
          } else if (update.type === 'event_deleted') {
            const deletedId = typeof update.data === 'string'
              ? update.data
              : update.id;
            return {
              events: nextEvents.filter((event) => event.id !== deletedId),
              recentUpdates: [update, ...(state.recentUpdates || []).slice(0, 49)],
              lastSyncTime: new Date(),
            };
          }

          return {
            events: nextEvents,
            recentUpdates: [update, ...(state.recentUpdates || []).slice(0, 49)],
            lastSyncTime: new Date(),
          };
        });
      },
      
      resolveEventConflict: (eventId, resolution) => {
        console.log(`🔀 Resolving event conflict for ${eventId}: ${resolution}`);
        
        set((state) => ({
          conflictingEvents: (state.conflictingEvents || []).filter(id => id !== eventId)
        }));
        
        // TODO: Implement conflict resolution logic based on resolution parameter
        if (resolution === 'accept') {
          // Accept the remote changes
        } else {
          // Keep local changes
        }
      },
      
      // Drag & Drop Actions
      startDrag: (eventId) => set({
        isDragging: true,
        draggedEventId: eventId
      }),
      
      endDrag: () => set({
        isDragging: false,
        draggedEventId: null
      }),
      
      // Utility Actions
      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
      
      // Data initialization is now handled by CalendarDataProvider
      // These methods are kept for backwards compatibility but delegate to the provider
      loadCalendarsFromApi: async () => {
        console.log('🔄 [CalendarStore] loadCalendarsFromApi - delegating to CalendarDataProvider');
        // CalendarDataProvider handles the actual API calls
      },
      
      loadEventsFromApi: async (filters?) => {
        console.log('🔄 [CalendarStore] loadEventsFromApi - delegating to CalendarDataProvider', filters);
        // CalendarDataProvider handles the actual API calls
      },
      
      initializeCalendarData: async () => {
        console.log('🔄 [CalendarStore] initializeCalendarData - delegating to CalendarDataProvider');
        // CalendarDataProvider handles the actual initialization
        // This is just for backwards compatibility
      },
    }),
    {
      name: 'calendar-store',
    }
  )
);

// Export the store
export { useCalendarStore };
export default useCalendarStore;
